import { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'wouter'
import {
  ArrowRight,
  Gamepad2,
  Headphones,
  Home,
  Menu,
  Moon,
  Search,
  Sun,
  User,
  X,
} from 'lucide-react'
import { trpc } from '@/lib/trpc'
import { useTheme } from '@/contexts/ThemeContext'
import { useLanguage } from '@/contexts/LanguageContext'
import { useIsAdmin } from './useUiMode'
import BrandLogo from './BrandLogo'
import CatalogMenu from './CatalogMenu'

declare const __APP_GIT_SHA__: string

const SUPPORT_URL = 'https://t.me/Ahmadaaww'

/* ── Search over the real Digital Goods catalog ─────────────────────────── */

function SearchBox({ autoFocus = false }: { autoFocus?: boolean }) {
  const [, navigate] = useLocation()
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const boxRef = useRef<HTMLDivElement>(null)

  // Only hit the catalog once the visitor actually types — keep the header light.
  const { data: catalog, isLoading } = trpc.dg.catalog.useQuery(undefined, {
    enabled: open && query.trim().length >= 1,
    staleTime: 5 * 60 * 1000,
  })

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (!boxRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  const q = query.trim().toLowerCase()
  const results = (catalog ?? [])
    .filter(c => c.categoryName.toLowerCase().includes(q))
    .slice(0, 7)

  const go = (term: string) => {
    setOpen(false)
    setQuery('')
    navigate(`/games?q=${encodeURIComponent(term)}`)
  }

  return (
    <div className="v2-search" ref={boxRef} style={{ display: autoFocus ? 'block' : undefined }}>
      <Search size={15} className="v2-search-ico" />
      <input
        className="v2-search-input"
        type="search"
        value={query}
        placeholder="Поиск игр, кодов, подарочных карт…"
        autoFocus={autoFocus}
        onChange={e => {
          setQuery(e.target.value)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={e => {
          if (e.key === 'Enter' && query.trim()) go(query.trim())
          if (e.key === 'Escape') setOpen(false)
        }}
        aria-label="Поиск по играм и кодам"
      />
      {open && q.length >= 1 && (
        <div className="v2-search-pop">
          {isLoading && <div className="v2-search-empty">Ищем…</div>}
          {!isLoading && results.length === 0 && (
            <div className="v2-search-empty">
              Ничего не нашлось. Все разделы — в «Игры и коды».
            </div>
          )}
          {results.map(c => (
            <button key={c.categoryId} type="button" className="v2-search-item" onClick={() => go(c.categoryName)}>
              <Gamepad2 size={15} style={{ color: 'var(--color-acid)' }} />
              <span style={{ flex: 1 }}>{c.categoryName}</span>
              <span style={{ fontSize: 11.5, color: 'var(--color-fog)' }}>
                от {c.fromPriceRub.toLocaleString('ru-RU')} ₽
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

/* ── Theme + language switches ──────────────────────────────────────────── */

function ThemeToggle() {
  const { theme, toggleTheme } = useTheme()
  const next = theme === 'dark' ? 'светлая' : 'тёмная'
  return (
    <button
      type="button"
      className="v2-icon-btn v2-theme-toggle"
      onClick={toggleTheme}
      title={`Включить ${next} тему`}
      aria-label={`Включить ${next} тему`}
    >
      {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
    </button>
  )
}

function LangToggle() {
  const { language, setLanguage } = useLanguage()
  return (
    <button
      type="button"
      className="v2-icon-btn"
      onClick={() => setLanguage(language === 'ru' ? 'en' : 'ru')}
      aria-label="Сменить язык"
    >
      {language.toUpperCase()}
    </button>
  )
}

/* ── Header ─────────────────────────────────────────────────────────────── */

const SUPPORT_LABEL = 'Поддержка'


/** Header in the established CoinsRubot style: serif brand, the classic
 *  Каталог / Отзывы / Поддержка navigation, plus the theme switch and the
 *  compact catalog search. */
export function HeaderV2() {
  const [location] = useLocation()
  const { data: me } = trpc.auth.me.useQuery()
  const isAdmin = useIsAdmin()
  const [drawer, setDrawer] = useState(false)
  // Меню-каталог с категориями (предпросмотр для админа — права на сервере).
  const { data: navMenu } = trpc.site.navMenu.useQuery(undefined, { staleTime: 30_000 })
  // Новые стили шапки (мобильная адаптация) — только в предпросмотре админа.
  useEffect(() => {
    const on = navMenu?.enabled === true
    const el = document.documentElement
    if (on) el.setAttribute('data-preview', '1')
    else el.removeAttribute('data-preview')
    return () => el.removeAttribute('data-preview')
  }, [navMenu?.enabled])

  const display = me?.telegramUsername ? `@${me.telegramUsername}` : me?.email

  return (
    <div className="nav-bar">
      <div className="nav-inner">
        <BrandLogo className="nav-brand is-logo" />

        <nav className="nav-links" aria-label="Навигация">
          <a href="/#catalog" className={`nav-link${location === '/' ? ' nav-link-active' : ''}`}>
            Каталог
          </a>
          <Link href="/reviews" className={`nav-link${location.startsWith('/reviews') ? ' nav-link-active' : ''}`}>
            Отзывы
          </Link>
          <a href={SUPPORT_URL} target="_blank" rel="noopener noreferrer" className="nav-link">
            {SUPPORT_LABEL}
          </a>
          {isAdmin && (
            <Link href="/admin" className={`nav-link${location.startsWith('/admin') ? ' nav-link-active' : ''}`}>
              Админ-панель
            </Link>
          )}
        </nav>

        <span className="nav-spacer" />

        <div className="v2-head-tools">
          <SearchBox />
          <ThemeToggle />
          {display ? (
            <Link href="/profile" className="pill" style={{ textDecoration: 'none', fontSize: 12, fontWeight: 700, padding: '7px 13px', color: 'var(--color-bone)', border: '1px solid var(--color-line-2)', whiteSpace: 'nowrap' }} title="Профиль">
              <User size={13} strokeWidth={2.4} style={{ verticalAlign: -2, marginRight: 5 }} />
              {display.length > 16 ? display.slice(0, 15) + '…' : display}
            </Link>
          ) : (
            <Link href="/login" className="btn btn-ghost" style={{ padding: '9px 16px', fontSize: 13 }}>
              Войти
            </Link>
          )}
          <button
            type="button"
            className="v2-icon-btn v2-hamburger"
            onClick={() => setDrawer(v => !v)}
            aria-label="Меню"
            aria-expanded={drawer}
          >
            {drawer ? <X size={17} /> : <Menu size={17} />}
          </button>
        </div>
      </div>

      {drawer && (
        <div className="v2-drawer">
          <div className="v2-drawer-search">
            <SearchBox autoFocus />
          </div>
          <CatalogMenu
            enabled={navMenu?.enabled === true}
            overrides={navMenu?.overrides ?? {}}
            onNavigate={() => setDrawer(false)}
          />
          <a href="/#catalog" className="v2-nav-link" onClick={() => setDrawer(false)}>
            Каталог
          </a>
          <Link href="/reviews" className="v2-nav-link" onClick={() => setDrawer(false)}>
            Отзывы
          </Link>
          <a href={SUPPORT_URL} target="_blank" rel="noopener noreferrer" className="v2-nav-link">
            {SUPPORT_LABEL}
          </a>
          {isAdmin && (
            <Link href="/admin" className="v2-nav-link" onClick={() => setDrawer(false)}>
              Админ-панель
            </Link>
          )}
          <div className="v2-drawer-auth">
            {display ? (
              <Link href="/profile" className="v2-auth" onClick={() => setDrawer(false)}>
                <User size={15} /> {display}
              </Link>
            ) : (
              <Link href="/login" className="v2-auth" onClick={() => setDrawer(false)}>
                Войти
              </Link>
            )}
            <ThemeToggle />
            <LangToggle />
          </div>
        </div>
      )}
    </div>
  )
}

/* ── Footer ─────────────────────────────────────────────────────────────── */

export function FooterV2() {
  return (
    <footer className="v2-footer">
      <div className="v2-footer-grid">
        <div className="v2-footer-brand">
          <BrandLogo />
          <p className="v2-footer-tag">
            Пополнение TikTok Coins, Telegram Stars, Steam и цифровые товары. Оплата по СБП и картой,
            зачисление автоматически.
          </p>
        </div>

        <div>
          <h4>Разделы</h4>
          <Link href="/#tiktok">TikTok Coins</Link>
          <Link href="/#services">Telegram Stars</Link>
          <Link href="/#services">Steam</Link>
          <Link href="/games">Игры и коды</Link>
        </div>

        <div>
          <h4>Информация</h4>
          <Link href="/reviews">Отзывы</Link>
          <Link href="/#faq">Вопросы</Link>
          <Link href="/agreement">Пользовательское соглашение</Link>
          <Link href="/privacy">Политика конфиденциальности</Link>
        </div>

        <div>
          <h4>Поддержка</h4>
          <a href={SUPPORT_URL} target="_blank" rel="noopener noreferrer">
            Telegram — @Ahmadaaww
          </a>
          <p className="v2-footer-tag">10:00–02:00 МСК · приём заказов и оплата 24/7</p>
        </div>
      </div>

      <div className="v2-footer-legal">
        Сервис не аффилирован с TikTok, Telegram и Steam. Все товарные знаки принадлежат их владельцам.
      </div>

      <div className="v2-footer-bottom">
        <span>
          © {new Date().getFullYear()} CoinsRubot. Все права защищены.
          {/* Which build is on screen — CLAUDE.md relies on this to verify a deploy. */}
          {` · сборка ${__APP_GIT_SHA__}`}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <LangToggle />
          <ThemeToggle />
        </span>
      </div>
    </footer>
  )
}

/* ── Mobile bottom navigation ───────────────────────────────────────────── */

export function BottomNavV2() {
  const [location] = useLocation()
  // Orders live inside the profile, so the bar keeps a single Профиль tab.
  const tabs = [
    { href: '/', label: 'Главная', icon: Home },
    { href: '/games', label: 'Игры', icon: Gamepad2 },
    { href: '/profile', label: 'Профиль', icon: User },
  ]

  return (
    <nav className="v2-bottomnav" aria-label="Навигация">
      {tabs.map(({ href, label, icon: Icon }) => {
        const base = href.split('?')[0]
        const active = base === '/' ? location === '/' : location.startsWith(base)
        return (
          <Link key={label} href={href} className={active ? 'is-active' : ''}>
            <Icon size={18} strokeWidth={active ? 2.6 : 2} />
            <span>{label}</span>
          </Link>
        )
      })}
    </nav>
  )
}

/* ── Small helper reused by inner pages ─────────────────────────────────── */

export function SupportLink() {
  return (
    <a className="v2-ghost-link" href={SUPPORT_URL} target="_blank" rel="noopener noreferrer">
      <Headphones size={15} />
      Поддержка в Telegram
      <ArrowRight size={14} />
    </a>
  )
}
